'use strict';

var _ = require('lodash');
var RecommendationTransformationService = function RecommendationTransformationService(configurationService, FaresOptimizerConstants) {
  function getRawDataByMarket(rawData, orderOptions) {
    var rawDataWithMarketTier = filterDataWithMarketTier(rawData);
    var rawDataWithoutMarketTier = filterDataWithoutMarketTier(rawData);
    var marketDataWithMarketTier = processAndSortRawData(rawDataWithMarketTier, orderOptions);
    var marketDataWithoutMarketTier = processAndSortRawData(rawDataWithoutMarketTier, orderOptions);
    return _.concat(marketDataWithMarketTier, marketDataWithoutMarketTier);
  }
  function transformRawData(rawData, orderOptions) {
    var groupingFields = _.map(orderOptions.groupBy, 'field');
    function mapLabels(sourceItems, labelFields) {
      return _.transform(labelFields, function (result, groupingItem) {
        var marketTier = getSignificantMarketTier(sourceItems);
        if (groupingItem.field === 'market' && !_.isNil(marketTier)) {
          if (marketTier === FaresOptimizerConstants.NOT_ASSIGNED) {
            marketTier = FaresOptimizerConstants.EN_DASH;
          }
          result.push({
            label: groupingItem.label + '/Tier',
            value: sourceItems[0][groupingItem.field] + ' / ' + marketTier
          });
        } else {
          result.push({
            label: groupingItem.label,
            value: sourceItems[0][groupingItem.field]
          });
        }
        return result;
      }, []);
    }
    function getDetailLabels(items) {
      var detailLabelFields = _.concat(orderOptions.groupBy, {
        field: 'category',
        label: 'Type'
      });
      return mapLabels(items, detailLabelFields);
    }
    function isUserAuthorizedForCurrentPos(levels, pos) {
      return _.includes(levels, pos);
    }
    function isUserOnlyApproverForCurrentPos(item) {
      return _.indexOf(configurationService.isOnlyApproverPos(), item.pos) !== -1;
    }
    function getApproveType(item) {
      if (isUserAuthorizedForCurrentPos(configurationService.getUserPrimaryPOSLevel(), item.pos)) {
        return 'intermediate';
      }
      if (isUserAuthorizedForCurrentPos(configurationService.getUserLastPOSLevel(), item.pos)) {
        return 'final';
      }
    }
    function orderByFare(rows) {
      return _.chain(rows).orderBy(['effectiveTotalFare', 'currentFareTotal'], 'asc').value();
    }
    function orderByCreationDate(rows) {
      return _.chain(rows).orderBy(['createdAt'], ['desc']).value();
    }
    function createGroupItem(rows) {
      var groupMaxFareChange = 0;
      var groupFareChangeAbsoluteSum = 0;
      var groupItem = initGroupItem(rows);
      var groupRowsByRecommendationKey = _.groupBy(orderByFare(rows), 'fareRecommendationRecordKey');
      var sortedRows = [];
      _.forEach(_.keys(groupRowsByRecommendationKey), function (recordKey) {
        sortedRows.push.apply(sortedRows, orderByCreationDate(groupRowsByRecommendationKey[recordKey]));
      });
      groupItem.rowGroups = _.chain(sortedRows).map(function (row) {
        groupMaxFareChange = row.maxFareChange > groupMaxFareChange ? row.maxFareChange : groupMaxFareChange;
        groupFareChangeAbsoluteSum += Math.abs(row.fareChangePercentage);
        return transformItem(row);
      }).value();
      var marketDataNewAndPendingStatus = filterNewAndPendingMarketData(sortedRows);
      groupItem.groupMaxFareChange = groupMaxFareChange;
      groupItem.recommendationCount = marketDataNewAndPendingStatus.length;
      groupItem.avgAbsoluteFareChange = computeAbsoluteFareChange(marketDataNewAndPendingStatus);
      groupItem.carrier = sortedRows[0].carrier;
      groupItem.marketTier = getSignificantMarketTier(sortedRows);
      groupItem.marketTier = !_.isNil(groupItem.marketTier) && groupItem.marketTier !== FaresOptimizerConstants.NOT_ASSIGNED ? groupItem.marketTier : FaresOptimizerConstants.NOT_ASSIGNED;
      groupItem.detailLabels = getDetailLabels(sortedRows);
      return groupItem;
    }
    function initGroupItem(items) {
      var groupKeys = _.pick(items[0], groupingFields);
      var groupItem = {
        detailLabels: [],
        labels: mapLabels(items, orderOptions.groupBy),
        rowGroups: [],
        expanded: true,
        approverType: getApproveType(items[0]),
        isOnlyApprover: isUserOnlyApproverForCurrentPos(items[0])
      };
      return _.merge(groupKeys, groupItem);
    }
    function matchToThreshold(thresholds, number) {
      var match;
      _.forEachRight(thresholds, function (threshold, index) {
        if (number >= threshold) {
          match = index;
          return false;
        }
      });
      return match;
    }
    function getFareChangeColorClass(percentage, opacityRange) {
      var valueCategory = percentage >= 0 ? 'positive' : 'negative';
      return 'fare-percent-change-' + valueCategory + '-opacity-' + opacityRange;
    }
    function createThresholds(min, max, number) {
      var diff = (max - min) / number;
      return _.times(number, function (index) {
        return index * diff;
      });
    }
    function setCssFields(group) {
      var roundedMax = Math.round(group.groupMaxFareChange * 100);
      var fareChangeThresholds = createThresholds(0, roundedMax, 6);
      _.forEach(group.rowGroups, function (rowGroup) {
        _.forEach(rowGroup.groupedRows, function (row) {
          var roundedChange = Math.round(Math.abs(row.fareChangePercentage * 100));
          var opacityCategory = matchToThreshold(fareChangeThresholds, roundedChange);
          var customClass = getFareChangeColorClass(row.fareChangePercentage, opacityCategory + 1);
          row.customCellClasses.displayStatus = getCssForStoppedAutoFiling(row);
          row.customCellClasses.fareChangePercentage = ['fare-percent-change-cell', customClass];
          row.maxFareChange = group.groupMaxFareChange;
          setCustomCellClasses(row, rowGroup);
        });
      });
    }
    function getCssForStoppedAutoFiling(row) {
      return isStoppedAutoFiling(row) ? ['recom-or-filing-stopped-color'] : '';
    }
    function isStoppedAutoFiling(row) {
      return row.isRecommendationCapped || row.status === FaresOptimizerConstants.NEW && row.isAutoApprovable === false;
    }
    function setCustomCellClasses(row, rowGroup) {
      if (row.actionCode === 'N') {
        row.customCellClasses.fbc = ['new'];
      } else if (row.actionCode === 'X') {
        row.customCellClasses.fbc = ['canceled'];
      } else {
        setCustomCellClassesForSharedFbc(row, rowGroup);
      }
    }
    function setCustomCellClassesForSharedFbc(row, rowGroup) {
      if (row.isSharedFbc) {
        row.customCellClasses.fbc = ['shared'];
      }
      if (row.isEntryLevel && rowGroup.hasSharedFbc) {
        row.customGroupHeaderCellClasses.fbc = ['shared'];
      }
    }
    function updateGroupEntryKeys(group) {
      var groupKey = _.chain(group).pick(groupingFields).values().value();
      var uniqueEntryKeys = _.chain(group.rowGroups).map('fbc').sort().value();
      var marketTier = group.marketTier;
      group.entryKey = _.concat(groupKey, marketTier, uniqueEntryKeys).join('.');
    }
    function transformItem(item) {
      var result = _.cloneDeep(item);
      result.entryKey = _.chain(item).pick(['id', 'season', 'dow', 'fbc']).values().join('.').value();
      return result;
    }
    function groupRecords(item) {
      return _.chain(item).pick(groupingFields).values().join('.').value();
    }
    function groupAndSortMarketList(data, orderOptions) {
      var sortingFields = _.map(orderOptions.sortBy, 'field');
      var sortingOrder = _.map(orderOptions.sortBy, 'order');
      return _.chain(data).groupBy(groupRecords).transform(function (result, item) {
        result.push(createGroupItem(item));
      }, []).forEach(updateGroupEntryKeys).forEach(setCssFields).orderBy(sortingFields, sortingOrder).value();
    }
    function partitionByMarketAndCreateGrouping(data, orderOptions) {
      return _.chain(data).groupBy(groupRecords).transform(function (result, marketList) {
        result.push(groupAndSortMarketList(marketList, orderOptions));
      }, []).flatten().value();
    }
    var rawDataWithMarketTier = filterDataWithMarketTier(rawData);
    var rawDataWithoutMarketTier = filterDataWithoutMarketTier(rawData);
    var marketsWithMarketTier = partitionByMarketAndCreateGrouping(rawDataWithMarketTier, orderOptions);
    var marketsWithoutMarketTier = partitionByMarketAndCreateGrouping(rawDataWithoutMarketTier, orderOptions);
    return _.concat(marketsWithMarketTier, marketsWithoutMarketTier);
  }
  function filterDataWithMarketTier(data) {
    return _.filter(data, function (item) {
      return !_.isNil(item.marketTier) && item.marketTier !== FaresOptimizerConstants.NOT_ASSIGNED;
    });
  }
  function filterDataWithoutMarketTier(data) {
    return _.filter(data, function (item) {
      return _.isNil(item.marketTier) || item.marketTier === FaresOptimizerConstants.NOT_ASSIGNED;
    });
  }
  function processAndSortRawData(rawData, orderOptions) {
    var sortingFields = _.map(orderOptions.sortBy, 'field');
    var sortingOrder = _.map(orderOptions.sortBy, 'order');
    var groupingFields = _.map(orderOptions.groupBy, 'field');
    function groupRecords(item) {
      return _.chain(item).pick(groupingFields).values().join('.').value();
    }
    return _.chain(rawData).groupBy(groupRecords).transform(function (result, marketLevelData) {
      result.push(computeFieldsInMarketLevelRawData(marketLevelData));
    }, []).orderBy(sortingFields, sortingOrder).map(function (market) {
      return market.marketLevelData;
    }).value();
  }
  function filterNewAndPendingMarketData(data) {
    return _.filter(data, function (market) {
      return market.status === 'New' || market.status === 'Pending';
    });
  }
  function computeFieldsInMarketLevelRawData(marketLevelData) {
    var marketDataNewAndPendingStatus = filterNewAndPendingMarketData(marketLevelData);
    return {
      carrier: marketLevelData[0].carrier,
      marketTier: getSignificantMarketTier(marketLevelData),
      recommendationCount: marketDataNewAndPendingStatus.length,
      avgAbsoluteFareChange: computeAbsoluteFareChange(marketDataNewAndPendingStatus),
      marketLevelData: marketLevelData
    };
  }
  function getSignificantMarketTier(marketLevelData) {
    var marketTier = _.chain(marketLevelData).filter(function (market) {
      return !_.isNil(market.marketTier) && market.marketTier !== FaresOptimizerConstants.NOT_ASSIGNED;
    }).reduce(function (min, market) {
      return market.marketTier < min ? market.marketTier : min;
    }, marketLevelData[0].marketTier).value();
    return _.isNil(marketTier) ? _.chain(marketLevelData).filter(function (market) {
      return FaresOptimizerConstants.NOT_ASSIGNED === market.marketTier;
    }).map(function (marketLevelData) {
      return marketLevelData.marketTier;
    }).head().value() : marketTier;
  }
  function computeAbsoluteFareChange(marketLevelData) {
    var groupFareChangeAbsoluteSum = 0;
    marketLevelData.forEach(function (market) {
      groupFareChangeAbsoluteSum += Math.abs(market.fareChangePercentage);
    });
    return _.isEmpty(marketLevelData) || groupFareChangeAbsoluteSum === 0 ? 0 : groupFareChangeAbsoluteSum / marketLevelData.length;
  }
  return {
    getRawDataByMarket: getRawDataByMarket,
    transformRawData: transformRawData
  };
};
RecommendationTransformationService.$inject = ['ConfigurationService', 'FaresOptimizerConstants'];
module.exports = RecommendationTransformationService;